import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Menu, MenuItem, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import MarkLogo from './MarkLogo';
import { chatApi } from '../services/api';

const GATED = ['/login', '/register', '/forgot-password', '/notice'];

const Icon = ({ children }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: 18, height: 18, stroke: 'currentColor', fill: 'none', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round' }}
  >
    {children}
  </Box>
);

const NavBar = () => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [motionOn, setMotionOn] = useState(() => localStorage.getItem('markMotion') !== 'off');
  const [pinned, setPinned] = useState(false);
  const [chats, setChats] = useState([]);
  const [palOpen, setPalOpen] = useState(false);
  const [palQuery, setPalQuery] = useState('');
  const [palIndex, setPalIndex] = useState(0);
  const palRef = useRef(null);

  const gated = GATED.includes(location.pathname);
  const isAdmin =
    (user?.email || '').toLowerCase() === 'admin@markenzoworldwide.com' ||
    (user?.user_metadata?.role || '').toLowerCase() === 'admin' ||
    (user?.app_metadata?.role || '').toLowerCase() === 'admin';

  useEffect(() => {
    if (gated) {
      document.body.classList.remove('mark-shell');
      return undefined;
    }
    document.body.classList.add('mark-shell');
    return () => document.body.classList.remove('mark-shell');
  }, [gated]);

  useEffect(() => {
    document.body.classList.toggle('mark-still', !motionOn);
    localStorage.setItem('markMotion', motionOn ? 'on' : 'off');
  }, [motionOn]);

  useEffect(() => {
    document.body.classList.toggle('mark-rail-open', Boolean(user) && !gated && pinned);
  }, [user, gated, pinned]);

  useEffect(() => {
    if (!palOpen) return undefined;
    const timer = setTimeout(() => palRef.current?.focus(), 30);
    return () => clearTimeout(timer);
  }, [palOpen]);

  useEffect(() => {
    if (!user || gated) return undefined;
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPalQuery('');
        setPalIndex(0);
        setPalOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [user, gated]);

  useEffect(() => {
    if (!user || gated) return undefined;
    let cancel = false;
    const load = () => {
      chatApi.getSessions().then((rows) => {
        if (!cancel) setChats((rows || []).slice(0, 6));
      });
    };
    load();
    window.addEventListener('mark-chats-changed', load);
    return () => {
      cancel = true;
      window.removeEventListener('mark-chats-changed', load);
    };
  }, [user, gated]);

  if (!user || gated) return null;

  const path = location.pathname;
  const markActive = path === '/' || path === '/home' || path.startsWith('/chat');

  const items = [
    {
      key: 'mark',
      label: 'Mark',
      to: '/home',
      active: markActive,
      icon: (
        <Icon>
          <path d="M4 5.5h16v10h-10l-5 4v-4H4z" />
        </Icon>
      ),
    },
    {
      key: 'learn',
      label: 'Learning At Markenzo',
      to: '/academy',
      active: path.startsWith('/academy'),
      icon: (
        <Icon>
          <path d="M12 4 2 9l10 5 10-5-10-5z" />
          <path d="M6 11.5V16c0 1.6 2.7 3 6 3s6-1.4 6-3v-4.5" />
        </Icon>
      ),
    },
    isAdmin
      ? {
          key: 'kb',
          label: 'Knowledge Base',
          to: '/knowledge',
          active: path.startsWith('/knowledge'),
          icon: (
            <Icon>
              <path d="M4.5 4.5A1.5 1.5 0 0 1 6 3h13v18H6a1.5 1.5 0 0 1-1.5-1.5z" />
              <path d="M19 17H6" />
            </Icon>
          ),
        }
      : {
          key: 'onboarding',
          label: 'Onboarding video',
          to: '/onboarding',
          active: path.startsWith('/onboarding'),
          icon: (
            <Icon>
              <circle cx="12" cy="12" r="8.5" />
              <path d="M10.2 8.8 15.5 12l-5.3 3.2z" />
            </Icon>
          ),
        },
    {
      key: 'access',
      label: 'Document Access',
      to: '/document-access',
      active: path.startsWith('/document-access') || path.startsWith('/shared-documents'),
      icon: (
        <Icon>
          <path d="M9 11.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" />
          <path d="M2.5 20c0-3.3 2.9-5.5 6.5-5.5s6.5 2.2 6.5 5.5" />
          <path d="M17.8 9a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM18 12.5c2 .6 3.5 2.1 3.5 4.2" />
        </Icon>
      ),
    },
    {
      key: 'links',
      label: 'Quick links',
      to: '/home',
      state: { view: 'links' },
      active: path === '/home' && location.state?.view === 'links',
      icon: (
        <Icon>
          <rect x="3.5" y="3.5" width="7" height="7" rx="2" />
          <rect x="13.5" y="3.5" width="7" height="7" rx="2" />
          <rect x="3.5" y="13.5" width="7" height="7" rx="2" />
          <rect x="13.5" y="13.5" width="7" height="7" rx="2" />
        </Icon>
      ),
    },
  ];

  const more = [
    { label: 'Suggestion Box', to: '/suggestions' },
    { label: 'Rewards & Recognition', to: '/rewards' },
    { label: 'Shared documents', to: '/shared-documents' },
    ...(isAdmin ? [{ label: 'Academy admin', to: '/academy-admin' }] : []),
  ];

  const ink = isDarkMode ? '#F9F5F0' : '#18140F';
  const muted = isDarkMode ? '#CBBFB2' : '#544B41';
  const faint = isDarkMode ? '#CBBFB2' : '#655C51';
  const line = isDarkMode ? '#574E46' : '#DED5C9';
  const line2 = isDarkMode ? '#9C8F82' : '#C9BFB3';
  const surface = isDarkMode ? '#3F3833' : '#FBF8F4';
  const wash = isDarkMode ? '#5C2833' : '#FBE7EC';
  const crimson = isDarkMode ? '#FFB0C4' : '#B80A3B';
  const ui = '"Instrument Sans", "Poppins", system-ui, sans-serif';

  const chipSx = {
    border: `1px solid ${isDarkMode ? '#9C8F82' : '#C9BFB3'}`,
    borderRadius: '99px',
    px: 1.4,
    py: 0,
    height: 34,
    minWidth: 0,
    minHeight: 34,
    fontSize: 12.5,
    fontWeight: 500,
    lineHeight: 1,
    color: muted,
    textTransform: 'none',
    fontFamily: ui,
    background: isDarkMode ? '#3F3833' : '#FBF8F4',
    boxShadow: 'none',
    flex: '0 1 auto',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    '&:hover': { borderColor: ink, color: ink, background: isDarkMode ? '#3F3833' : '#FBF8F4', boxShadow: 'none' },
    '& .MuiButton-startIcon': { mr: 0.6, ml: 0 },
  };

  const handleLogout = async () => {
    setAnchorEl(null);
    try {
      await logout();
      navigate('/login');
    } catch (e) {
      navigate('/login');
    }
  };

  const needle = palQuery.trim().toLowerCase();
  const matches = (row) => !needle || `${row.title} ${row.group} ${row.detail}`.toLowerCase().includes(needle);
  const palette = [
    ...(needle ? [{
      id: 'ask',
      group: 'Ask MARK',
      title: palQuery.trim(),
      detail: 'Send this to MARK on the home page',
      go: 'Ask',
      run: () => {
        sessionStorage.setItem('markPendingAsk', palQuery.trim());
        navigate('/home', { state: { ask: Date.now() } });
      },
    }] : []),
    ...[
      ...items.filter((item) => item.to).map((item) => ({
        id: item.key,
        group: 'Screens',
        title: item.label,
        detail: 'Open this screen',
        go: 'Open',
        run: () => navigate(item.to, item.state ? { state: item.state } : undefined),
      })),
      ...(isAdmin ? [{
        id: 'onboarding',
        group: 'Screens',
        title: 'Onboarding video',
        detail: 'Company onboarding video',
        go: 'Open',
        run: () => navigate('/onboarding'),
      }] : []),
      ...more.map((item) => ({
        id: item.to,
        group: 'Screens',
        title: item.label,
        detail: 'Open this screen',
        go: 'Open',
        run: () => navigate(item.to),
      })),
      ...chats.map((chat) => ({
        id: `chat-${chat.id}`,
        group: 'Recent chats',
        title: chat.title || 'New chat',
        detail: 'Open this conversation',
        go: 'Open',
        run: () => {
          sessionStorage.setItem('markOpenSession', chat.id);
          navigate('/home', { state: { sessionId: chat.id, open: Date.now() } });
        },
      })),
      {
        id: 'new-chat',
        group: 'Actions',
        title: 'New chat',
        detail: 'Start a fresh conversation',
        go: 'Run',
        run: () => navigate('/home', { state: { fresh: Date.now() } }),
      },
      {
        id: 'motion',
        group: 'Actions',
        title: motionOn ? 'Pause motion' : 'Turn motion on',
        detail: 'Stop or start the animations',
        go: 'Run',
        run: () => setMotionOn((value) => !value),
      },
      {
        id: 'theme',
        group: 'Actions',
        title: isDarkMode ? 'Switch to light' : 'Switch to dark',
        detail: 'Change the theme',
        go: 'Run',
        run: () => toggleDarkMode(),
      },
    ].filter(matches),
  ];
  const activeIndex = palette.length ? Math.min(palIndex, palette.length - 1) : 0;
  const runPalette = (row) => {
    if (!row) return;
    setPalOpen(false);
    row.run();
  };

  return (
    <>
      <Box
        component="nav"
        aria-label="Main"
        sx={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 50,
          width: pinned ? 300 : 72,
          background: surface,
          borderRight: `1px solid ${line}`,
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          py: '14px',
          px: '11px',
          overflow: 'hidden',
          fontFamily: ui,
          transition: 'width .26s cubic-bezier(.16,1,.3,1), box-shadow .26s',
          '&:hover, &:focus-within': { width: 300, boxShadow: pinned ? 'none' : '0 10px 30px rgba(19,19,22,.08)' },
          '& .rail-label, & .rail-brand, & .rail-pin, & .rail-search-extra': { opacity: 0, transition: 'opacity .16s', whiteSpace: 'nowrap' },
          '& .rail-more': { flex: '0 0 auto', maxHeight: 0, opacity: 0, overflow: 'hidden', transition: 'max-height .3s cubic-bezier(.16,1,.3,1), opacity .18s' },
          '&:hover .rail-label, &:focus-within .rail-label, &:hover .rail-brand, &:focus-within .rail-brand, &:hover .rail-pin, &:focus-within .rail-pin, &:hover .rail-search-extra, &:focus-within .rail-search-extra': { opacity: 1, transitionDelay: '.06s' },
          '&:hover .rail-more, &:focus-within .rail-more': { flex: '1 1 auto', minHeight: 0, maxHeight: 'none', opacity: 1, overflowY: 'auto' },
          ...(pinned ? {
            '& .rail-label, & .rail-brand, & .rail-pin, & .rail-search-extra': { opacity: 1 },
            '& .rail-more': { flex: '1 1 auto', minHeight: 0, maxHeight: 'none', opacity: 1, overflowY: 'auto' },
          } : {}),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', px: '5px', pb: '14px', borderBottom: `1px solid ${line}`, flexShrink: 0 }}>
          <Box
            component="button"
            onClick={() => navigate('/home')}
            aria-label="Markenzo — go to Mark"
            sx={{ border: 0, background: 'transparent', p: 0, cursor: 'pointer', width: 40, height: 32, borderRadius: '9px', display: 'grid', placeItems: 'center', flex: 'none', '&:hover': { background: isDarkMode ? '#4A423C' : '#F1EFEC' } }}
          >
            <MarkLogo variant="color" height={22} alt="MARK" />
          </Box>
          <Box className="rail-brand" sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontFamily: ui, fontWeight: 600, fontSize: 14, lineHeight: 1.1, color: ink }}>Markenzo</Typography>
            <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 8, letterSpacing: '0.2em', textTransform: 'uppercase', color: faint, mt: '1px' }}>Worldwide</Typography>
          </Box>
          <Box
            className="rail-pin"
            component="button"
            type="button"
            aria-label={pinned ? 'Collapse the sidebar' : 'Pin the sidebar open'}
            aria-pressed={pinned}
            onClick={() => setPinned((v) => !v)}
            sx={{ border: 0, background: pinned ? wash : 'transparent', color: pinned ? crimson : muted, width: 30, height: 30, borderRadius: '8px', cursor: 'pointer', display: 'grid', placeItems: 'center', flex: 'none' }}
          >
            <Icon>
              <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" />
              <path d="M9.5 4.5v15" />
            </Icon>
          </Box>
        </Box>

        <Box
          component="button"
          type="button"
          onClick={() => {
            setPalQuery('');
            setPalIndex(0);
            setPalOpen(true);
          }}
          aria-label="Search"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            width: '100%',
            px: '10px',
            py: '9px',
            minHeight: 40,
            borderRadius: '10px',
            border: `1px solid ${line2}`,
            background: isDarkMode ? '#2C2623' : '#fff',
            color: faint,
            cursor: 'pointer',
            fontFamily: ui,
            fontSize: 13,
            textAlign: 'left',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            '&:hover': { borderColor: crimson, color: muted },
          }}
        >
          <Box sx={{ width: 22, height: 22, display: 'grid', placeItems: 'center', flex: 'none' }}>
            <Icon>
              <circle cx="11" cy="11" r="6.5" />
              <path d="M16 16l4.5 4.5" />
            </Icon>
          </Box>
          <Box component="span" className="rail-search-extra" sx={{ flex: 1, whiteSpace: 'nowrap' }}>Search...</Box>
          <Box component="span" className="rail-search-extra" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 8.5, border: `1px solid ${line}`, borderRadius: '5px', px: '5px', py: '2px', color: faint, flex: 'none' }}>⌘K</Box>
        </Box>

        {items.map((item) => {
          const inner = (
            <Box
              key={item.key}
              component={item.to ? RouterLink : 'button'}
              {...(item.to ? { to: item.to, state: item.state } : { type: 'button' })}
              onClick={item.action}
              aria-current={item.active ? 'page' : undefined}
              aria-label={item.label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                textAlign: 'left',
                px: '10px',
                py: '9px',
                borderRadius: '10px',
                border: 0,
                background: item.active ? wash : 'transparent',
                color: item.active ? crimson : muted,
                fontWeight: item.active ? 600 : 500,
                fontSize: 13.5,
                fontFamily: ui,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                ...(item.key === 'links' ? { mt: '8px', pt: '12px', borderTop: `1px solid ${line}`, borderRadius: '0 0 10px 10px' } : {}),
                cursor: 'pointer',
                textDecoration: 'none',
                minHeight: 40,
                flexShrink: 0,
                '&:hover': { background: item.active ? wash : (isDarkMode ? '#4A423C' : '#F1EFEC'), color: item.active ? crimson : ink },
              }}
            >
              <Box sx={{ width: 22, display: 'grid', placeItems: 'center', flex: 'none' }}>{item.icon}</Box>
              <Box component="span" className="rail-label" sx={{ whiteSpace: 'nowrap' }}>{item.label}</Box>
            </Box>
          );
          return inner;
        })}

        <Box className="rail-more" sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minHeight: 0, pr: '2px', pb: '10px' }}>
          <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: faint, px: '7px', pb: '7px' }}>Chats</Typography>
          <Box
            component="button"
            type="button"
            onClick={() => navigate('/home', { state: { fresh: Date.now() } })}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              border: 0,
              borderRadius: '10px',
              background: 'linear-gradient(120deg, #D6114A, #A80834)',
              color: '#fff',
              fontFamily: ui,
              fontWeight: 600,
              fontSize: 13.5,
              px: '10px',
              py: '9px',
              mb: '4px',
              minHeight: 40,
              cursor: 'pointer',
              textAlign: 'left',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            <Box component="span" sx={{ fontSize: 17, lineHeight: 1, width: 22, textAlign: 'center', flex: 'none' }}>+</Box>
            <Box component="span" className="rail-label">New chat</Box>
          </Box>
          {chats.map((chat) => (
            <Box
              key={chat.id}
              component="button"
              type="button"
              title={chat.title || 'New chat'}
              onClick={() => {
                sessionStorage.setItem('markOpenSession', chat.id);
                navigate('/home', { state: { sessionId: chat.id, open: Date.now() } });
              }}
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                width: '100%',
                border: 0,
                background: 'transparent',
                textAlign: 'left',
                px: '9px',
                py: '7px',
                borderRadius: '9px',
                color: muted,
                fontFamily: ui,
                fontSize: 13.5,
                lineHeight: 1.35,
                cursor: 'pointer',
                whiteSpace: 'normal',
                overflow: 'hidden',
                wordBreak: 'break-word',
                flex: 'none',
                '&:hover': { background: isDarkMode ? '#4A423C' : '#F1EFEC', color: ink },
              }}
            >
              {chat.title || 'New chat'}
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 0.75,
          width: 'max-content',
          maxWidth: 'calc(100vw - 112px)',
          minWidth: 0,
        }}
      >
        <Button
          component={RouterLink}
          to="/home"
          state={{ view: 'links' }}
          sx={chipSx}
          startIcon={(
            <Icon>
              <rect x="3.5" y="3.5" width="7" height="7" rx="2" />
              <rect x="13.5" y="3.5" width="7" height="7" rx="2" />
              <rect x="3.5" y="13.5" width="7" height="7" rx="2" />
              <rect x="13.5" y="13.5" width="7" height="7" rx="2" />
            </Icon>
          )}
        >
          Quick links
        </Button>
        <Button sx={chipSx} onClick={toggleDarkMode}>
          {isDarkMode ? 'Light' : 'Dark'}
        </Button>
        <Button
          sx={{
            ...chipSx,
            width: 34,
            minWidth: 34,
            maxWidth: 34,
            px: 0,
            flex: '0 0 34px',
            borderRadius: '99px',
          }}
          aria-label="Account"
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          <Box
            component="svg"
            viewBox="0 0 24 24"
            sx={{ width: 16, height: 16, stroke: 'currentColor', fill: 'none', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round', display: 'block' }}
          >
            <circle cx="12" cy="8" r="3.2" />
            <path d="M5.5 19.2c.6-3.1 3-5 6.5-5s5.9 1.9 6.5 5" />
          </Box>
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem disabled sx={{ opacity: '1 !important', fontSize: 13, fontWeight: 600 }}>
            {user?.email || 'Account'}
          </MenuItem>
          {more.map((item) => (
            <MenuItem
              key={item.to}
              onClick={() => { setAnchorEl(null); navigate(item.to); }}
              sx={{ fontSize: 13.5, fontFamily: ui }}
            >
              {item.label}
            </MenuItem>
          ))}
          <MenuItem onClick={handleLogout} sx={{ color: crimson, fontSize: 13.5 }}>Sign out</MenuItem>
        </Menu>
      </Box>

      {palOpen && (
        <Box
          onMouseDown={() => setPalOpen(false)}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 200,
            background: 'rgba(12,9,8,0.42)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            pt: '12vh',
            px: 2,
          }}
        >
          <Box
            role="dialog"
            aria-modal="true"
            aria-label="Search Mark"
            onMouseDown={(event) => event.stopPropagation()}
            sx={{
              width: '100%',
              maxWidth: 720,
              background: surface,
              border: `1px solid ${line2}`,
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 30px 80px rgba(0,0,0,0.3)',
              fontFamily: ui,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '13px', px: '20px', borderBottom: `1px solid ${line}` }}>
              <Box sx={{ color: faint, display: 'grid', placeItems: 'center', flex: 'none' }}>
                <Icon>
                  <circle cx="11" cy="11" r="6.5" />
                  <path d="M16 16l4.5 4.5" />
                </Icon>
              </Box>
              <Box
                component="input"
                ref={palRef}
                value={palQuery}
                onChange={(event) => {
                  setPalQuery(event.target.value);
                  setPalIndex(0);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    setPalIndex((index) => (palette.length ? (index + 1) % palette.length : 0));
                  } else if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    setPalIndex((index) => (palette.length ? (index - 1 + palette.length) % palette.length : 0));
                  } else if (event.key === 'Enter') {
                    event.preventDefault();
                    runPalette(palette[activeIndex]);
                  } else if (event.key === 'Escape') {
                    event.preventDefault();
                    setPalOpen(false);
                  }
                }}
                placeholder="Search screens, chats and links..."
                aria-label="Search"
                autoComplete="off"
                sx={{
                  flex: 1,
                  border: 0,
                  outline: 'none',
                  background: 'transparent',
                  fontFamily: ui,
                  fontSize: 16.5,
                  color: ink,
                  py: '16px',
                  '&::placeholder': { color: faint },
                }}
              />
              <Box component="span" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, border: `1px solid ${line}`, borderRadius: '5px', px: '6px', py: '2px', color: faint, flex: 'none' }}>
                Esc
              </Box>
            </Box>
            <Box sx={{ maxHeight: 'min(58vh, 480px)', overflowY: 'auto', p: '12px' }}>
              {palette.length === 0 ? (
                <Typography sx={{ fontFamily: ui, fontSize: 13.5, color: faint, textAlign: 'center', py: 3 }}>
                  Nothing matches that.
                </Typography>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '8px' }}>
                  {palette.map((row, index) => (
                    <Box
                      key={row.id}
                      component="button"
                      type="button"
                      onClick={() => runPalette(row)}
                      onMouseEnter={() => setPalIndex(index)}
                      sx={{
                        display: 'block',
                        textAlign: 'left',
                        border: `1px solid ${index === activeIndex ? crimson : line}`,
                        borderRadius: '14px',
                        background: index === activeIndex ? surface : (isDarkMode ? '#2C2623' : '#F3EEE7'),
                        color: ink,
                        p: '13px 14px',
                        cursor: 'pointer',
                        fontFamily: ui,
                        minHeight: 78,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '8px' }}>
                        <Box component="span" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 8.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: faint, flex: 1 }}>
                          {row.group}
                        </Box>
                        <Box component="span" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 8.5, letterSpacing: '0.11em', textTransform: 'uppercase', color: faint }}>
                          {row.go}
                        </Box>
                      </Box>
                      <Box component="strong" sx={{ display: 'block', fontSize: 14.5, fontWeight: 600, letterSpacing: '-0.012em', lineHeight: 1.3, mb: '3px' }}>
                        {row.title}
                      </Box>
                      <Box component="span" sx={{ display: 'block', fontSize: 12, color: faint, lineHeight: 1.45 }}>
                        {row.detail}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default NavBar;
